import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Session, { useSessionContext } from 'supertokens-auth-react/recipe/session';
import EmailVerification from 'supertokens-auth-react/recipe/emailverification';
import { buildAuthRedirectToUrl, getWebsiteBasePath } from './initAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard that redirects unauthenticated (or guest-session) users to /auth.
 * Re-exported from shared-auth so every app uses the same logic.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const sessionContext = useSessionContext();
  const location = useLocation();
  const doesSessionExist = !sessionContext.loading && sessionContext.doesSessionExist;
  const [isCheckingGuestSession, setIsCheckingGuestSession] = useState(false);
  const [isGuestSession, setIsGuestSession] = useState(false);
  const [hasInvalidSession, setHasInvalidSession] = useState(false);
  const [isCheckingEmailVerification, setIsCheckingEmailVerification] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(true);

  useEffect(() => {
    let active = true;

    const checkGuestSession = async () => {
      if (sessionContext.loading || !doesSessionExist) {
        if (active) {
          setIsGuestSession(false);
          setHasInvalidSession(false);
          setIsCheckingGuestSession(false);
        }
        return;
      }

      setIsCheckingGuestSession(true);
      try {
        const payload = await Session.getAccessTokenPayloadSecurely();
        if (active) {
          setHasInvalidSession(false);
          setIsGuestSession(payload?.isGuest === true);
        }
      } catch {
        if (active) {
          setHasInvalidSession(true);
          setIsGuestSession(false);
        }
      } finally {
        if (active) {
          setIsCheckingGuestSession(false);
        }
      }
    };

    void checkGuestSession();

    return () => {
      active = false;
    };
  }, [sessionContext.loading, doesSessionExist]);

  useEffect(() => {
    let active = true;

    const checkEmailVerification = async () => {
      // Skip the check while session/guest status is still resolving, or for
      // sessions that don't require it (no session, or a guest session).
      if (sessionContext.loading || isCheckingGuestSession || !doesSessionExist || isGuestSession) {
        if (active) {
          setIsEmailVerified(true);
          setIsCheckingEmailVerification(false);
        }
        return;
      }

      setIsCheckingEmailVerification(true);
      try {
        const response = await EmailVerification.isEmailVerified();
        if (active) {
          setIsEmailVerified(response.isVerified);
        }
      } catch {
        // Fail open on transient errors so a network hiccup doesn't lock users out.
        if (active) {
          setIsEmailVerified(true);
        }
      } finally {
        if (active) {
          setIsCheckingEmailVerification(false);
        }
      }
    };

    void checkEmailVerification();

    return () => {
      active = false;
    };
  }, [sessionContext.loading, doesSessionExist, isGuestSession, isCheckingGuestSession]);

  if (sessionContext.loading || isCheckingGuestSession || isCheckingEmailVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
            style={{ borderColor: 'var(--color-primary, #6366f1)' }}
          />
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (hasInvalidSession) {
    if (typeof window !== 'undefined') {
      const authRedirectUrl = buildAuthRedirectToUrl(window.location.href);
      if (authRedirectUrl.startsWith(window.location.origin)) {
        const redirectPath = authRedirectUrl.slice(window.location.origin.length) || '/auth';
        return <Navigate to={redirectPath} replace />;
      }

      window.location.replace(authRedirectUrl);
      return null;
    }

    const redirectToPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/auth?redirectToPath=${encodeURIComponent(redirectToPath)}`} replace />;
  }

  const isAuthenticated = doesSessionExist && !isGuestSession && !hasInvalidSession;
  if (!isAuthenticated) {
    if (typeof window !== 'undefined') {
      const authRedirectUrl = buildAuthRedirectToUrl(window.location.href);
      if (authRedirectUrl.startsWith(window.location.origin)) {
        const redirectPath = authRedirectUrl.slice(window.location.origin.length) || '/auth';
        return <Navigate to={redirectPath} replace />;
      }

      window.location.replace(authRedirectUrl);
      return null;
    }

    const redirectToPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/auth?redirectToPath=${encodeURIComponent(redirectToPath)}`} replace />;
  }

  if (!isEmailVerified) {
    return <Navigate to={`${getWebsiteBasePath()}/verify-email`} replace />;
  }

  return <>{children}</>;
};
